/**
 * フォーム設定タブの共通レイアウト部品 (2026-07-28)。
 *
 * 設計方針（現行 UI の問題と対策）:
 *   1. 長い説明文が設定値と同じ視覚重量で常時展開されていて一覧できない
 *      → SettingRow は「見出し + 1 行要約 + コントロール」だけを常時表示し、
 *        詳細は <SettingDetails> の折りたたみに逃がす。
 *   2. 条件を満たさない設定がフォームから丸ごと消え「どこにいったか分からない」
 *      → disabledReason を渡すと、消さずに淡色 + 理由表示で残す。
 *   3. どこまで設定済みか分からない
 *      → SettingsSection の見出し右に現在値サマリ (summary) を出す。
 */
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

export function SettingsSection({
  icon: Icon,
  title,
  description,
  summary,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  /** 見出し右に出す現在値サマリ（設定済みかを一目で判断させる）。 */
  summary?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden">
      <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className="size-4 mt-0.5 flex-shrink-0 text-[var(--accent)]" />
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">
              {title}
            </h2>
            {description && (
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>
        {summary && (
          <div className="flex-shrink-0 text-[11px] text-[var(--text-secondary)] text-right">
            {summary}
          </div>
        )}
      </header>
      <div className="divide-y divide-[var(--border)]">{children}</div>
    </section>
  );
}

export function SettingRow({
  title,
  hint,
  control,
  children,
  disabledReason,
}: {
  title: string;
  /** コントロールの真下に出す 1 行の平易な説明。 */
  hint?: ReactNode;
  /** 右カラムに置くコントロール（幅の狭い入力向け）。 */
  control?: ReactNode;
  /** コントロールが横幅を要する場合は children に置く（title の下に全幅で並ぶ）。 */
  children?: ReactNode;
  /** 設定できない理由。渡すと淡色 + 操作不可の見た目になり、行は消えない。 */
  disabledReason?: string | null;
}) {
  const disabled = !!disabledReason;
  return (
    <div className={`px-4 py-3.5 ${disabled ? 'opacity-55' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-[12px] font-semibold text-[var(--text-primary)]">
            {title}
          </h3>
          {disabled ? (
            <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
              {disabledReason}
            </p>
          ) : (
            hint && (
              <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
                {hint}
              </p>
            )
          )}
        </div>
        {control && !disabled && (
          <div className="flex-shrink-0 w-[240px]">{control}</div>
        )}
      </div>
      {children && !disabled && <div className="mt-2.5">{children}</div>}
    </div>
  );
}

/** 詳細説明の折りたたみ。既定は閉じており、必要な人だけが開く。 */
export function SettingDetails({
  label = '詳しく',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline"
      >
        <ChevronDown
          className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
        />
        {label}
      </button>
      {open && (
        <div className="mt-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-[11px] text-[var(--text-secondary)] leading-relaxed space-y-1.5">
          {children}
        </div>
      )}
    </div>
  );
}

/** チェックボックス 1 個の設定行（ON/OFF が主役のもの）。 */
export function SettingToggle({
  checked,
  onChange,
  label,
  hint,
  children,
  disabledReason,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: ReactNode;
  children?: ReactNode;
  disabledReason?: string | null;
}) {
  const disabled = !!disabledReason;
  return (
    <div className={`px-4 py-3.5 ${disabled ? 'opacity-55' : ''}`}>
      <label
        className={`flex items-start gap-2.5 select-none ${
          disabled ? '' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 mt-0.5 accent-[var(--accent)] flex-shrink-0"
        />
        <span className="min-w-0">
          <span className="block text-[12px] font-semibold text-[var(--text-primary)]">
            {label}
          </span>
          <span className="block text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
            {disabled ? disabledReason : hint}
          </span>
        </span>
      </label>
      {children && !disabled && <div className="mt-2 pl-6">{children}</div>}
    </div>
  );
}
